import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode
} from 'react'
import { Platform, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native'
import { Image } from 'expo-image'
import NativeQRCode from 'react-native-qrcode-svg'
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { captureRef } from 'react-native-view-shot'
import { useTranslation } from 'react-i18next'

import { isWeb } from '@/constants/app'
import { WEB_VIEW_MAX_WIDTH } from '@/constants/config'
import { cn } from '@/utils/cn'
import { saveImageToLibrary } from '@/utils/saveImage'
import { generateRandomFileName } from '@/utils/url'
import { useToast } from '@/components/base'
import { Close, Delete, Download } from '@/components/icon'
import MaxWidthWrapper from '@/components/MaxWidthWrapper'
import ScreenHeader from '@/components/ScreenHeader'

type Source = string | null

interface ImageViewerState {
  setType: (type: 'image' | 'qrcode') => void
  source: Source
  setSource: (source: Source) => void
  onDeleteCallback: MutableRefObject<null | (() => void)>
}

const ImageViewerContext = createContext<ImageViewerState>({
  setType: () => {},
  source: null,
  setSource: () => {},
  onDeleteCallback: { current: null }
})

export function useImageViewer() {
  return useContext(ImageViewerContext)
}

interface ImageViewerProviderProps {
  children: ReactNode
}

export function ImageViewerProvider({ children }: ImageViewerProviderProps) {
  const [type, setType] = useState<'image' | 'qrcode'>('image')
  const [source, setSource] = useState<Source>(null)
  const onDeleteCallback = useRef(null)

  const reset = () => {
    setSource(null)
    setType('image')
    onDeleteCallback.current = null
  }

  const value = useMemo(() => ({ setType, source, setSource, onDeleteCallback }), [source])

  return (
    <ImageViewerContext.Provider value={value}>
      {children}
      {source && <ImageViewer type={type} source={source} reset={reset} onDeleteCallback={onDeleteCallback} />}
    </ImageViewerContext.Provider>
  )
}

interface ImageViewerProps {
  type: 'image' | 'qrcode'
  source: string
  reset: () => void
  onDeleteCallback: MutableRefObject<null | (() => void)>
}

function ImageViewer({ type, source, reset, onDeleteCallback }: ImageViewerProps) {
  const { t } = useTranslation()
  const { width: deviceWidth, height: deviceHeight } = useWindowDimensions()
  const contentRef = useRef<View>(null)
  const toast = useToast()
  const [loaded, setLoaded] = useState(false)
  const offset = useSharedValue(deviceHeight)

  const handleClose = () => {
    offset.value = withTiming(deviceHeight, { duration: 300 }, () => {
      runOnJS(reset)()
    })
  }
  const { top, bottom } = useSafeAreaInsets()
  const viewStyle = useAnimatedStyle(() => {
    return { transform: [{ translateY: offset.value }] }
  })

  const downloadQrCode = async () => {
    try {
      const localUri = await captureRef(contentRef, {
        height: 440,
        quality: 1
      })
      if (Platform.OS !== 'web') {
        await saveImageToLibrary(localUri)
        toast.show({ variant: 'success', description: t('ui:downloadSuccess') })
        return
      }
      const response = await fetch(localUri, {
        method: 'GET',
        headers: {}
      })
      const buffer = await response.arrayBuffer()
      const url = window.URL.createObjectURL(new Blob([buffer]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'QR-code.jpg') //or any other extension
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.show({ variant: 'success', description: t('ui:downloadSuccess') })
    } catch (e) {
      toast.show({ variant: 'error', description: t('ui:downloadFailed') })
      console.error(e)
    }
  }

  const downloadImage = async () => {
    if (Platform.OS !== 'web') {
      try {
        const extension = source.includes('image/png') || source.toLowerCase().includes('.png') ? '.png' : '.jpg'
        await saveImageToLibrary(source, extension)
        toast.show({ variant: 'success', description: t('ui:downloadSuccess') })
      } catch (error) {
        console.error(error)
        toast.show({ variant: 'error', description: t('ui:downloadFailed') })
      }
      return
    }
    const fileName = generateRandomFileName()
    let extension = ''
    if (source.includes('image/jpeg')) {
      extension = '.jpg'
    } else if (source.includes('image/png')) {
      extension = '.png'
    }
    if (extension) {
      const downloadLink = document.createElement('a')
      downloadLink.href = source
      downloadLink.download = `${fileName}${extension}`
      downloadLink.click()
    } else {
      window.location.assign(source)
    }
    toast.show({ variant: 'success', description: t('ui:downloadSuccess') })
  }

  useEffect(() => {
    if (loaded || type === 'qrcode') {
      offset.value = withTiming(0, { duration: 500 })
    }
  }, [loaded, type])

  return (
    <Animated.View style={[styles.overlay, viewStyle]}>
      <MaxWidthWrapper>
        <View style={{ marginTop: top }}>
          <ScreenHeader
            leftElement={
              <Pressable onPress={handleClose}>
                {({ pressed }) => (
                  <View className={cn(pressed && 'opacity-50')}>
                    <Close className={'size-6 text-white'} />
                  </View>
                )}
              </Pressable>
            }
          />
        </View>
        <View className={'flex-1'}>
          {type === 'image' ? (
            <View ref={contentRef} collapsable={false} className={'flex-1'}>
              <Image
                style={{ width: '100%', height: '100%' }}
                source={{ uri: source }}
                contentFit={'contain'}
                onLoad={() => {
                  setLoaded(true)
                }}
              />
            </View>
          ) : (
            <View className={'my-auto bg-white p-4'} ref={contentRef} collapsable={false}>
              <NativeQRCode
                value={source}
                size={(isWeb ? Math.min(WEB_VIEW_MAX_WIDTH, deviceWidth) : deviceWidth) - 16 - 16}
              />
            </View>
          )}
        </View>
        <View className={'h-11 flex-row px-2'} style={{ marginBottom: bottom }}>
          {onDeleteCallback.current !== null && (
            <Pressable
              className={'size-10 items-center justify-center'}
              onPress={() => {
                if (onDeleteCallback.current) {
                  onDeleteCallback.current()
                }
                handleClose()
              }}
            >
              {({ pressed }) => (
                <View className={cn(pressed && 'opacity-50')}>
                  <Delete className={'size-6 text-white'} />
                </View>
              )}
            </Pressable>
          )}

          <Pressable
            className={'ml-auto size-10 items-center justify-center'}
            onPress={type === 'image' ? downloadImage : downloadQrCode}
          >
            {({ pressed }) => (
              <View className={cn(pressed && 'opacity-50')}>
                <Download className={'size-6 text-white'} />
              </View>
            )}
          </Pressable>
        </View>
      </MaxWidthWrapper>
    </Animated.View>
  )
}

interface ImageViewerTriggerProps {
  source: string
  className?: string
  onDelete?: () => void
}

export function ImageViewerTrigger({ source, className }: ImageViewerTriggerProps) {
  const { setSource } = useContext(ImageViewerContext)

  return (
    <Pressable onPress={() => setSource(source)}>
      <View className={cn('h-20 overflow-hidden rounded-lg', className)}>
        <Image style={{ width: '100%', height: '100%' }} source={{ uri: source }} contentFit={'cover'} />
      </View>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 1000,
    backgroundColor: '#000000'
  }
})
